import { MatchDataSource, ParticipantDataSource, TournamentDataSource } from '../data/datasources';
import { TournamentAdminDataSource } from '../data/datasources/TournamentAdminDataSource';
import { FollowDataSource } from '../data/datasources/FollowDataSource';
import { FriendshipDataSource } from '../data/datasources/FriendshipDataSource';
import { ChallengeDataSource } from '../data/datasources/ChallengeDataSource';
import { SocialDataSource } from '../data/datasources/SocialDataSource';
import { PostDataSource } from '../data/datasources/PostDataSource';
import { TournamentFollowDataSource } from '../data/datasources/TournamentFollowDataSource';
import { MatchRepositoryImpl, ParticipantRepositoryImpl, TournamentRepositoryImpl } from '../data/repositories';
import { TournamentAdminRepositoryImpl } from '../data/repositories/TournamentAdminRepositoryImpl';
import { FollowRepositoryImpl } from '../data/repositories/FollowRepositoryImpl';
import { FriendshipRepositoryImpl } from '../data/repositories/FriendshipRepositoryImpl';
import { ChallengeRepositoryImpl } from '../data/repositories/ChallengeRepositoryImpl';
import { SocialRepositoryImpl } from '../data/repositories/SocialRepositoryImpl';
import { PostRepositoryImpl } from '../data/repositories/PostRepositoryImpl';
import { TournamentFollowRepositoryImpl } from '../data/repositories/TournamentFollowRepositoryImpl';
import {
  AddParticipantUseCase,
  AddRegisteredParticipantUseCase,
  AddTournamentAdminUseCase,
  CheckTournamentPermissionUseCase,
  CreateTournamentUseCase,
  DeleteParticipantUseCase,
  DeleteTournamentUseCase,
  GenerateTournamentBracketUseCase,
  GetAllTournamentsUseCase,
  GetBracketLeaderboardUseCase,
  GetMatchesByTournamentUseCase,
  GetParticipantsByTournamentUseCase,
  GetPublicTournamentsUseCase,
  GetRoundRobinStandingsUseCase,
  GetSwissStandingsUseCase,
  GetTournamentAdminsUseCase,
  GetTournamentChampionUseCase,
  GetUserStatsUseCase,
  GetUserTournamentsUseCase,
  RemoveTournamentAdminUseCase,
  SearchUserByEmailUseCase,
  UpdateMatchResultUseCase,
  UpdateTournamentUseCase,
} from '../domain/usecases';
import { FollowUserUseCase } from '../domain/usecases/FollowUserUseCase';
import { UnfollowUserUseCase } from '../domain/usecases/UnfollowUserUseCase';
import { GetFollowersUseCase } from '../domain/usecases/GetFollowersUseCase';
import { GetFollowingUseCase } from '../domain/usecases/GetFollowingUseCase';
import { SendFriendRequestUseCase } from '../domain/usecases/SendFriendRequestUseCase';
import { AcceptFriendRequestUseCase } from '../domain/usecases/AcceptFriendRequestUseCase';
import { DeclineFriendRequestUseCase } from '../domain/usecases/DeclineFriendRequestUseCase';
import { RemoveFriendUseCase } from '../domain/usecases/RemoveFriendUseCase';
import { GetFriendsUseCase } from '../domain/usecases/GetFriendsUseCase';
import { GetPendingFriendRequestsUseCase } from '../domain/usecases/GetPendingFriendRequestsUseCase';
import { SendChallengeUseCase } from '../domain/usecases/SendChallengeUseCase';
import { AcceptChallengeUseCase } from '../domain/usecases/AcceptChallengeUseCase';
import { DeclineChallengeUseCase } from '../domain/usecases/DeclineChallengeUseCase';
import { GetChallengesReceivedUseCase } from '../domain/usecases/GetChallengesReceivedUseCase';
import { GetChallengesSentUseCase } from '../domain/usecases/GetChallengesSentUseCase';
import { GetSocialCountsUseCase } from '../domain/usecases/GetSocialCountsUseCase';
import { CreatePostUseCase } from '../domain/usecases/CreatePostUseCase';
import { GetFeedUseCase } from '../domain/usecases/GetFeedUseCase';
import { GetUserPostsUseCase } from '../domain/usecases/GetUserPostsUseCase';
import { DeletePostUseCase } from '../domain/usecases/DeletePostUseCase';
import { LikePostUseCase } from '../domain/usecases/LikePostUseCase';
import { UnlikePostUseCase } from '../domain/usecases/UnlikePostUseCase';
import { GetTournamentPostsUseCase } from '../domain/usecases/GetTournamentPostsUseCase';
import { CreateVictoryPostUseCase } from '../domain/usecases/CreateVictoryPostUseCase';
import { GetPublicPostUseCase } from '../domain/usecases/GetPublicPostUseCase';
import { FollowTournamentUseCase } from '../domain/usecases/FollowTournamentUseCase';
import { UnfollowTournamentUseCase } from '../domain/usecases/UnfollowTournamentUseCase';
import { GetFollowedTournamentsUseCase } from '../domain/usecases/GetFollowedTournamentsUseCase';
import { GetTournamentFollowerCountUseCase } from '../domain/usecases/GetTournamentFollowerCountUseCase';

// Moderation feature - Initialize first (other use cases depend on it)
import { ModerationDataSource } from '../data/datasources/ModerationDataSource';
import { ModerationRepositoryImpl } from '../data/repositories/ModerationRepositoryImpl';
import { ModerateContentUseCase } from '../domain/usecases/ModerateContentUseCase';

const moderationDataSource = new ModerationDataSource();
const moderationRepository = new ModerationRepositoryImpl(moderationDataSource);
export const moderateContentUseCase = new ModerateContentUseCase(moderationRepository);

// Initialize data sources
const tournamentDataSource = new TournamentDataSource();
const participantDataSource = new ParticipantDataSource();
const matchDataSource = new MatchDataSource();

// Initialize repositories
const tournamentRepository = new TournamentRepositoryImpl(tournamentDataSource);
const participantRepository = new ParticipantRepositoryImpl(participantDataSource);
const matchRepository = new MatchRepositoryImpl(matchDataSource);

// Initialize use cases - Tournaments
export const createTournamentUseCase = new CreateTournamentUseCase(tournamentRepository, moderateContentUseCase);
export const getAllTournamentsUseCase = new GetAllTournamentsUseCase(tournamentRepository);

// Initialize use cases - Participants
export const addParticipantUseCase = new AddParticipantUseCase(participantRepository, moderateContentUseCase, tournamentRepository);
export const getParticipantsByTournamentUseCase = new GetParticipantsByTournamentUseCase(participantRepository);
export const deleteParticipantUseCase = new DeleteParticipantUseCase(participantRepository);

// Initialize use cases - Matches & Bracket
export const generateBracketUseCase = new GenerateTournamentBracketUseCase(
  tournamentRepository,
  participantRepository,
  matchRepository
);
export const getMatchesByTournamentUseCase = new GetMatchesByTournamentUseCase(matchRepository);
export const updateMatchResultUseCase = new UpdateMatchResultUseCase(
  matchRepository,
  tournamentRepository,
  participantRepository
);
export const getTournamentChampionUseCase = new GetTournamentChampionUseCase(
  matchRepository,
  participantRepository,
  tournamentRepository
);

// Export repositories for direct use if needed
export { matchRepository, participantRepository, tournamentRepository };

export const updateTournamentUseCase = new UpdateTournamentUseCase(tournamentRepository, moderateContentUseCase);
export const deleteTournamentUseCase = new DeleteTournamentUseCase(
  tournamentRepository,
  participantRepository,
  matchRepository
);

export const getRoundRobinStandingsUseCase = new GetRoundRobinStandingsUseCase(
  participantRepository,
  matchRepository
);

export const getSwissStandingsUseCase = new GetSwissStandingsUseCase(
  participantRepository,
  matchRepository
);

// Crear instancias
const tournamentAdminDataSource = new TournamentAdminDataSource();
const tournamentAdminRepository = new TournamentAdminRepositoryImpl(tournamentAdminDataSource);

export const addTournamentAdminUseCase = new AddTournamentAdminUseCase(
  tournamentRepository,
  tournamentAdminRepository
);

export const removeTournamentAdminUseCase = new RemoveTournamentAdminUseCase(
  tournamentRepository,
  tournamentAdminRepository
);

export const getTournamentAdminsUseCase = new GetTournamentAdminsUseCase(
  tournamentAdminRepository
);

export const searchUserByEmailUseCase = new SearchUserByEmailUseCase();

export const checkTournamentPermissionUseCase = new CheckTournamentPermissionUseCase(
  tournamentRepository,
  tournamentAdminRepository
);

export const addRegisteredParticipantUseCase = new AddRegisteredParticipantUseCase(
  participantRepository,
  tournamentRepository
);

export const getPublicTournamentsUseCase = new GetPublicTournamentsUseCase(
  tournamentRepository
);

export const getUserStatsUseCase = new GetUserStatsUseCase(
  participantRepository,
  matchRepository,
  tournamentRepository
);

export const getUserTournamentsUseCase = new GetUserTournamentsUseCase(
  participantRepository,
  tournamentRepository
);

export const getBracketLeaderboardUseCase = new GetBracketLeaderboardUseCase(
  participantRepository,
  matchRepository,
  tournamentRepository
);

// Social features - Initialize data sources
const followDataSource = new FollowDataSource();
const friendshipDataSource = new FriendshipDataSource();
const challengeDataSource = new ChallengeDataSource();
const socialDataSource = new SocialDataSource();
const postDataSource = new PostDataSource();

// Social features - Initialize repositories
const followRepository = new FollowRepositoryImpl(followDataSource);
const friendshipRepository = new FriendshipRepositoryImpl(friendshipDataSource);
const challengeRepository = new ChallengeRepositoryImpl(challengeDataSource);
const socialRepository = new SocialRepositoryImpl(socialDataSource);
const postRepository = new PostRepositoryImpl(postDataSource);

// Social features - Follow system use cases
export const followUserUseCase = new FollowUserUseCase(followRepository);
export const unfollowUserUseCase = new UnfollowUserUseCase(followRepository);
export const getFollowersUseCase = new GetFollowersUseCase(followRepository);
export const getFollowingUseCase = new GetFollowingUseCase(followRepository);

// Social features - Friends system use cases
export const sendFriendRequestUseCase = new SendFriendRequestUseCase(friendshipRepository);
export const acceptFriendRequestUseCase = new AcceptFriendRequestUseCase(friendshipRepository);
export const declineFriendRequestUseCase = new DeclineFriendRequestUseCase(friendshipRepository);
export const removeFriendUseCase = new RemoveFriendUseCase(friendshipRepository);
export const getFriendsUseCase = new GetFriendsUseCase(friendshipRepository);
export const getPendingFriendRequestsUseCase = new GetPendingFriendRequestsUseCase(friendshipRepository);

// Social features - Challenge system use cases
export const sendChallengeUseCase = new SendChallengeUseCase(challengeRepository, moderateContentUseCase);
export const acceptChallengeUseCase = new AcceptChallengeUseCase(challengeRepository);
export const declineChallengeUseCase = new DeclineChallengeUseCase(challengeRepository);
export const getChallengesReceivedUseCase = new GetChallengesReceivedUseCase(challengeRepository);
export const getChallengesSentUseCase = new GetChallengesSentUseCase(challengeRepository);

// Social features - Social stats use case
export const getSocialCountsUseCase = new GetSocialCountsUseCase(socialRepository);

// Social features - Post system use cases
export const createPostUseCase = new CreatePostUseCase(postRepository, moderateContentUseCase);
export const getFeedUseCase = new GetFeedUseCase(postRepository);
export const getUserPostsUseCase = new GetUserPostsUseCase(postRepository);
export const getTournamentPostsUseCase = new GetTournamentPostsUseCase(postRepository);
export const deletePostUseCase = new DeletePostUseCase(postRepository);
export const likePostUseCase = new LikePostUseCase(postRepository);
export const unlikePostUseCase = new UnlikePostUseCase(postRepository);
export const getPublicPostUseCase = new GetPublicPostUseCase(postRepository);
export const createVictoryPostUseCase = new CreateVictoryPostUseCase(
  postRepository,
  tournamentRepository,
  participantRepository,
  matchRepository,
  moderateContentUseCase
);

// Export social repositories for direct use if needed
export { followRepository, friendshipRepository, challengeRepository, socialRepository, postRepository };

// Tournament Follow system - Initialize data source and repository
const tournamentFollowDataSource = new TournamentFollowDataSource();
const tournamentFollowRepository = new TournamentFollowRepositoryImpl(tournamentFollowDataSource);

// Tournament Follow system - Use cases
export const followTournamentUseCase = new FollowTournamentUseCase(tournamentFollowRepository);
export const unfollowTournamentUseCase = new UnfollowTournamentUseCase(tournamentFollowRepository);
export const getFollowedTournamentsUseCase = new GetFollowedTournamentsUseCase(tournamentFollowRepository);
export const getTournamentFollowerCountUseCase = new GetTournamentFollowerCountUseCase(tournamentFollowRepository);

// Export tournament follow repository for direct use if needed
export { tournamentFollowRepository };

// League feature - Initialize data source and repository
import { LeagueDataSource } from '../data/datasources/LeagueDataSource';
import { LeagueRepositoryImpl } from '../data/repositories/LeagueRepositoryImpl';
import { CreateLeagueUseCase } from '../domain/usecases/CreateLeagueUseCase';
import { GetUserLeaguesUseCase } from '../domain/usecases/GetUserLeaguesUseCase';
import { GetLeagueByIdUseCase } from '../domain/usecases/GetLeagueByIdUseCase';
import { UpdateLeagueUseCase } from '../domain/usecases/UpdateLeagueUseCase';
import { DeleteLeagueUseCase } from '../domain/usecases/DeleteLeagueUseCase';
import { AddLeagueParticipantUseCase } from '../domain/usecases/AddLeagueParticipantUseCase';
import { RemoveLeagueParticipantUseCase } from '../domain/usecases/RemoveLeagueParticipantUseCase';
import { GetLeagueParticipantsUseCase } from '../domain/usecases/GetLeagueParticipantsUseCase';
import { CreateLeagueTournamentUseCase } from '../domain/usecases/CreateLeagueTournamentUseCase';
import { GetLeagueStandingsUseCase } from '../domain/usecases/GetLeagueStandingsUseCase';
import { CompleteLeagueUseCase } from '../domain/usecases/CompleteLeagueUseCase';
import { GetPublicLeaguesUseCase } from '../domain/usecases/GetPublicLeaguesUseCase';

const leagueDataSource = new LeagueDataSource();
const leagueRepository = new LeagueRepositoryImpl(leagueDataSource);

// League feature - Use cases
export const createLeagueUseCase = new CreateLeagueUseCase(leagueRepository, moderateContentUseCase);
export const getUserLeaguesUseCase = new GetUserLeaguesUseCase(leagueRepository);
export const getLeagueByIdUseCase = new GetLeagueByIdUseCase(leagueRepository);
export const updateLeagueUseCase = new UpdateLeagueUseCase(leagueRepository, moderateContentUseCase);
export const deleteLeagueUseCase = new DeleteLeagueUseCase(leagueRepository);
export const addLeagueParticipantUseCase = new AddLeagueParticipantUseCase(leagueRepository, moderateContentUseCase);
export const removeLeagueParticipantUseCase = new RemoveLeagueParticipantUseCase(leagueRepository);
export const getLeagueParticipantsUseCase = new GetLeagueParticipantsUseCase(leagueRepository);
export const createLeagueTournamentUseCase = new CreateLeagueTournamentUseCase(
  leagueRepository,
  tournamentRepository,
  participantRepository,
  moderateContentUseCase
);
export const getLeagueStandingsUseCase = new GetLeagueStandingsUseCase(
  leagueRepository,
  tournamentRepository,
  participantRepository,
  matchRepository
);
export const completeLeagueUseCase = new CompleteLeagueUseCase(leagueRepository, tournamentRepository);
export const getPublicLeaguesUseCase = new GetPublicLeaguesUseCase(leagueRepository);

// Export league repository for direct use if needed
export { leagueRepository };

// Notification feature - Initialize data source and repository
import { NotificationDataSource } from '../data/datasources/NotificationDataSource';
import { NotificationRepositoryImpl } from '../data/repositories/NotificationRepositoryImpl';
import { RegisterPushTokenUseCase } from '../domain/usecases/RegisterPushTokenUseCase';
import { RemovePushTokenUseCase } from '../domain/usecases/RemovePushTokenUseCase';
import { GetNotificationsUseCase } from '../domain/usecases/GetNotificationsUseCase';
import { GetUnreadNotificationCountUseCase } from '../domain/usecases/GetUnreadNotificationCountUseCase';
import { MarkNotificationReadUseCase } from '../domain/usecases/MarkNotificationReadUseCase';
import { MarkAllNotificationsReadUseCase } from '../domain/usecases/MarkAllNotificationsReadUseCase';
import { SendNotificationUseCase } from '../domain/usecases/SendNotificationUseCase';
import { GetNotificationPreferencesUseCase } from '../domain/usecases/GetNotificationPreferencesUseCase';
import { UpdateNotificationPreferenceUseCase } from '../domain/usecases/UpdateNotificationPreferenceUseCase';

const notificationDataSource = new NotificationDataSource();
const notificationRepository = new NotificationRepositoryImpl(notificationDataSource);

// Notification feature - Use cases
export const registerPushTokenUseCase = new RegisterPushTokenUseCase(notificationRepository);
export const removePushTokenUseCase = new RemovePushTokenUseCase(notificationRepository);
export const getNotificationsUseCase = new GetNotificationsUseCase(notificationRepository);
export const getUnreadNotificationCountUseCase = new GetUnreadNotificationCountUseCase(notificationRepository);
export const markNotificationReadUseCase = new MarkNotificationReadUseCase(notificationRepository);
export const markAllNotificationsReadUseCase = new MarkAllNotificationsReadUseCase(notificationRepository);
export const sendNotificationUseCase = new SendNotificationUseCase(notificationRepository);
export const getNotificationPreferencesUseCase = new GetNotificationPreferencesUseCase(notificationRepository);
export const updateNotificationPreferenceUseCase = new UpdateNotificationPreferenceUseCase(notificationRepository);

// Export notification repository for direct use if needed
export { notificationRepository };

// Profile feature - Use case (uses moderation + direct supabase auth)
import { UpdateProfileUseCase } from '../domain/usecases/UpdateProfileUseCase';
export const updateProfileUseCase = new UpdateProfileUseCase(moderateContentUseCase);

// Account deletion - calls supabase directly (no repo layer needed)
import { DeleteAccountUseCase } from '../domain/usecases/DeleteAccountUseCase';
export const deleteAccountUseCase = new DeleteAccountUseCase();

// Tournament Invitation feature - Initialize data source and repository
import { TournamentInvitationDataSource } from '../data/datasources/TournamentInvitationDataSource';
import { TournamentInvitationRepositoryImpl } from '../data/repositories/TournamentInvitationRepositoryImpl';
import { SendTournamentInvitationUseCase } from '../domain/usecases/SendTournamentInvitationUseCase';
import { AcceptTournamentInvitationUseCase } from '../domain/usecases/AcceptTournamentInvitationUseCase';
import { DeclineTournamentInvitationUseCase } from '../domain/usecases/DeclineTournamentInvitationUseCase';
import { CancelTournamentInvitationUseCase } from '../domain/usecases/CancelTournamentInvitationUseCase';
import { GetInvitationsReceivedUseCase } from '../domain/usecases/GetInvitationsReceivedUseCase';
import { GetInvitationsSentUseCase } from '../domain/usecases/GetInvitationsSentUseCase';
import { JoinTournamentByCodeUseCase } from '../domain/usecases/JoinTournamentByCodeUseCase';

const tournamentInvitationDataSource = new TournamentInvitationDataSource();
const tournamentInvitationRepository = new TournamentInvitationRepositoryImpl(tournamentInvitationDataSource);

// Tournament Invitation feature - Use cases
export const sendTournamentInvitationUseCase = new SendTournamentInvitationUseCase(
  tournamentInvitationRepository,
  tournamentRepository,
  participantRepository,
  moderateContentUseCase
);
export const acceptTournamentInvitationUseCase = new AcceptTournamentInvitationUseCase(
  tournamentInvitationRepository,
  participantRepository,
  tournamentRepository
);
export const declineTournamentInvitationUseCase = new DeclineTournamentInvitationUseCase(tournamentInvitationRepository);
export const cancelTournamentInvitationUseCase = new CancelTournamentInvitationUseCase(tournamentInvitationRepository);
export const getInvitationsReceivedUseCase = new GetInvitationsReceivedUseCase(tournamentInvitationRepository);
export const getInvitationsSentUseCase = new GetInvitationsSentUseCase(tournamentInvitationRepository);
export const joinTournamentByCodeUseCase = new JoinTournamentByCodeUseCase(
  tournamentInvitationRepository
);

// Export tournament invitation data source for direct use if needed
export { tournamentInvitationDataSource, tournamentInvitationRepository };

// Subscription feature - Initialize data source and repository
import { SubscriptionDataSource } from '../data/datasources/SubscriptionDataSource';
import { SubscriptionRepositoryImpl } from '../data/repositories/SubscriptionRepositoryImpl';
import { GetSubscriptionUseCase } from '../domain/usecases/GetSubscriptionUseCase';
import { GetPlanLimitsUseCase } from '../domain/usecases/GetPlanLimitsUseCase';
import { CheckFeatureAccessUseCase } from '../domain/usecases/CheckFeatureAccessUseCase';

const subscriptionDataSource = new SubscriptionDataSource();
const subscriptionRepository = new SubscriptionRepositoryImpl(subscriptionDataSource);

// Subscription feature - Use cases
export const getSubscriptionUseCase = new GetSubscriptionUseCase(subscriptionRepository);
export const getPlanLimitsUseCase = new GetPlanLimitsUseCase(subscriptionRepository);
export const checkFeatureAccessUseCase = new CheckFeatureAccessUseCase(subscriptionRepository);

// Export subscription repository for direct use if needed
export { subscriptionRepository };

// Team feature - Initialize data source and repository
import { TeamDataSource } from '../data/datasources/TeamDataSource';
import { TeamRepositoryImpl } from '../data/repositories/TeamRepositoryImpl';
import { CreateTeamUseCase } from '../domain/usecases/CreateTeamUseCase';
import { UpdateTeamUseCase } from '../domain/usecases/UpdateTeamUseCase';
import { DeleteTeamUseCase } from '../domain/usecases/DeleteTeamUseCase';
import { GetTeamByIdUseCase } from '../domain/usecases/GetTeamByIdUseCase';
import { GetUserTeamsUseCase } from '../domain/usecases/GetUserTeamsUseCase';
import { GetPublicTeamsUseCase } from '../domain/usecases/GetPublicTeamsUseCase';
import { GetTeamMembersUseCase } from '../domain/usecases/GetTeamMembersUseCase';
import { RemoveTeamMemberUseCase } from '../domain/usecases/RemoveTeamMemberUseCase';
import { LeaveTeamUseCase } from '../domain/usecases/LeaveTeamUseCase';
import { SendTeamInvitationUseCase } from '../domain/usecases/SendTeamInvitationUseCase';
import { AcceptTeamInvitationUseCase } from '../domain/usecases/AcceptTeamInvitationUseCase';
import { DeclineTeamInvitationUseCase } from '../domain/usecases/DeclineTeamInvitationUseCase';
import { CancelTeamInvitationUseCase } from '../domain/usecases/CancelTeamInvitationUseCase';
import { GetTeamInvitationsUseCase } from '../domain/usecases/GetTeamInvitationsUseCase';

const teamDataSource = new TeamDataSource();
const teamRepository = new TeamRepositoryImpl(teamDataSource);

// Team feature - Use cases
export const createTeamUseCase = new CreateTeamUseCase(teamRepository, moderateContentUseCase);
export const updateTeamUseCase = new UpdateTeamUseCase(teamRepository, moderateContentUseCase);
export const deleteTeamUseCase = new DeleteTeamUseCase(teamRepository);
export const getTeamByIdUseCase = new GetTeamByIdUseCase(teamRepository);
export const getUserTeamsUseCase = new GetUserTeamsUseCase(teamRepository);
export const getPublicTeamsUseCase = new GetPublicTeamsUseCase(teamRepository);
export const getTeamMembersUseCase = new GetTeamMembersUseCase(teamRepository);
export const removeTeamMemberUseCase = new RemoveTeamMemberUseCase(teamRepository);
export const leaveTeamUseCase = new LeaveTeamUseCase(teamRepository);
export const sendTeamInvitationUseCase = new SendTeamInvitationUseCase(teamRepository, sendNotificationUseCase);
export const acceptTeamInvitationUseCase = new AcceptTeamInvitationUseCase(teamRepository);
export const declineTeamInvitationUseCase = new DeclineTeamInvitationUseCase(teamRepository);
export const cancelTeamInvitationUseCase = new CancelTeamInvitationUseCase(teamRepository);
export const getTeamInvitationsUseCase = new GetTeamInvitationsUseCase(teamRepository);

// Export team repository for direct use if needed
export { teamRepository };

// Training feature - Initialize data source and repository
import { TrainingDataSource } from '../data/datasources/TrainingDataSource';
import { TrainingRepositoryImpl } from '../data/repositories/TrainingRepositoryImpl';
import { GetRpmLeaderboardUseCase } from '../domain/usecases/training/GetRpmLeaderboardUseCase';
import { GetUserLaunchesUseCase } from '../domain/usecases/training/GetUserLaunchesUseCase';
import { GetUserTrainingStatsUseCase } from '../domain/usecases/training/GetUserTrainingStatsUseCase';

const trainingDataSource = new TrainingDataSource();
const trainingRepository = new TrainingRepositoryImpl(trainingDataSource);

// Training feature - Use cases
export const getRpmLeaderboardUseCase = new GetRpmLeaderboardUseCase(trainingRepository);
export const getUserLaunchesUseCase = new GetUserLaunchesUseCase(trainingRepository);
export const getUserTrainingStatsUseCase = new GetUserTrainingStatsUseCase(trainingRepository);

// Export training repository for direct use if needed
export { trainingRepository };
